import { useEffect, useRef, useState } from "react";
import { TodoCard } from "../../components/TodoCard";
import { Link } from "wouter";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { editStamp } from "../../lib/editStamp";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { Camera, MapPin, MessageSquareText, Phone } from "lucide-react";
import { paths, type Customer, type Job, type JobStatus } from "@clevis/shared-types";
import { workflowFor } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, cn } from "@clevis/shared-ui";
import { db, storage } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { compressImage } from "../../lib/imageCompress";
import { useAuth } from "../../hooks/useAuth";
import { JobInsights } from "../../components/JobInsights";
import { JobChildren } from "../../components/JobChildren";
import { OrderedParts } from "../../components/OrderedParts";
import { JobAgreements } from "../../components/JobAgreements";
import { JobPayment } from "../../components/JobPayment";
import { JobPay } from "../../components/JobPay";
import { JobExpenses } from "../../components/JobExpenses";
import { JobEstimates } from "../../components/JobEstimates";
import { JobSignoff } from "../../components/JobSignoff";
import { JobSteps, dryRunLine } from "../../components/JobSteps";
import { JobLoads } from "../../components/JobLoads";
import { AvailableTechs } from "../../components/AvailableTechs";
import { PinDrop } from "../../components/PinDrop";
import { JobEditCard } from "../../components/JobEditCard";
import { JobWorkDone, useJobWork } from "../../components/JobWorkDone";
import { useJobSettings } from "../../hooks/useJobSettings";
import { useAccess } from "../../hooks/useAccess";
import { useEntitlements } from "../../hooks/useEntitlements";
import { SendDocuments } from "../../components/SendDocuments";
import { ROLE_RANK } from "@clevis/shared-types";

interface NoteRow {
  id: string;
  body: string;
  authorName: string;
  createdAt: number;
}
interface PhotoRow {
  id: string;
  path: string;
  url?: string;
  uploadedByName: string;
  createdAt: number;
}

const statusStyle: Record<string, string> = {
  scheduled: "bg-steel/15 text-ink",
  in_progress: "bg-brandorange/15 text-brandorange-dark",
  done: "bg-success/15 text-success",
  canceled: "bg-steel/10 text-steel line-through",
};

// The tech's daily-driver screen: everything about one job — who, where
// (tap to navigate), call the customer, change status, leave field notes,
// and attach photos straight from the camera.
export function JobDetail({ jobId, onBack }: { jobId: string; onBack: () => void }) {
  const { tenantId, user, role } = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const can = useAccess();
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  // A Field tech looking at someone else's job (the owner gave techs the
  // Schedule): the when-and-where card only; notes, photos and steps are theirs.
  const peek = role === "tech" && !!user && !!job && !job.assignedUids.includes(user.uid);
  const detailsReady = role !== "tech" || (!!job && !peek);
  const [noteBody, setNoteBody] = useState("");
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const work = useJobWork(tenantId, jobId);
  // The job type's flow: sections a field tech doesn't get on this kind of job.
  const { flows, workflows } = useJobSettings(tenantId);
  const workflow = workflowFor(job?.jobType, workflows);
  // What the last step said ("Customer texted…", "Finished. Make the invoice?").
  const [stepNote, setStepNote] = useState("");
  const { hasFeature } = useEntitlements();
  const show = (section: string) => role !== "tech" || !hasFeature("jobFlows") || !(flows[job?.jobType ?? ""] ?? []).includes(section);
  const [projectName, setProjectName] = useState("");
  const fileInput = useRef<HTMLInputElement | null>(null);
  // Bumped when an estimate becomes an invoice, so the Payment card reloads.
  const [billingRev, setBillingRev] = useState(0);
  // The company's sign-off rule: off / optional / required before "Finish job".
  const [signoffMode, setSignoffMode] = useState<"off" | "optional" | "required">("optional");
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenant(tenantId)), (snap) =>
      setSignoffMode((snap.get("jobSignoff") as "off" | "optional" | "required" | undefined) ?? "optional"),
    );
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenantJob(tenantId, jobId)), (snap) =>
      setJob((snap.data() as Job) ?? null),
    );
  }, [tenantId, jobId]);

  useEffect(() => {
    if (!tenantId || !job?.customerId) {
      setCustomer(null);
      return;
    }
    return onSnapshot(doc(db, paths.tenantCustomer(tenantId, job.customerId)), (snap) =>
      setCustomer((snap.data() as Customer) ?? null),
    );
  }, [tenantId, job?.customerId]);

  useEffect(() => {
    if (!tenantId || !detailsReady) return;
    const subs = [
      onSnapshot(
        query(collection(db, paths.tenantJobNotes(tenantId, jobId)), orderBy("createdAt", "desc")),
        (snap) => setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as NoteRow)),
      ),
      onSnapshot(
        query(collection(db, paths.tenantJobPhotos(tenantId, jobId)), orderBy("createdAt", "desc")),
        (snap) => {
          const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PhotoRow);
          setPhotos(rows);
          // Resolve download URLs lazily for any rows that lack one.
          for (const row of rows.filter((r) => !r.url)) {
            void getDownloadURL(storageRef(storage, row.path))
              .then((url) =>
                setPhotos((prev) => prev.map((p) => (p.id === row.id ? { ...p, url } : p))),
              )
              .catch(() => undefined);
          }
        },
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId, jobId, detailsReady]);

  useEffect(() => {
    if (!tenantId || !job?.projectId) {
      setProjectName("");
      return;
    }
    return onSnapshot(doc(db, paths.tenantProject(tenantId, job.projectId)), (snap) =>
      setProjectName((snap.get("name") as string | undefined) ?? ""),
    );
  }, [tenantId, job?.projectId]);

  const [texting, setTexting] = useState<"on_my_way" | "rep_intro" | null>(null);
  const [textNote, setTextNote] = useState("");
  async function sendText(kind: "on_my_way" | "rep_intro") {
    if (!tenantId) return;
    setTexting(kind);
    setTextNote("");
    try {
      await call("sendJobText", { tenantId, jobId, kind });
      setTextNote(kind === "on_my_way" ? "Customer texted: you're on the way." : "Customer texted: intro sent.");
    } catch (err) {
      setTextNote((err as Error).message);
    } finally {
      setTexting(null);
    }
  }

  async function addNote() {
    if (!tenantId || !user || !noteBody.trim()) return;
    await addDoc(collection(db, paths.tenantJobNotes(tenantId, jobId)), {
      body: noteBody.trim().slice(0, 5000),
      authorUid: user.uid,
      authorName: user.displayName || user.email || "Team member",
      createdAt: Date.now(),
    });
    setNoteBody("");
  }

  async function uploadPhotos(files: FileList | null) {
    if (!tenantId || !user || !files || files.length === 0) return;
    setError("");
    for (const file of Array.from(files).slice(0, 10)) {
      setUploading((n) => n + 1);
      void (async () => {
        try {
          const blob = await compressImage(file);
          const path = paths.jobPhotoStorage(tenantId, jobId, `${crypto.randomUUID()}.jpg`);
          await uploadBytes(storageRef(storage, path), blob, { contentType: "image/jpeg" });
          await addDoc(collection(db, paths.tenantJobPhotos(tenantId, jobId)), {
            path,
            uploadedByUid: user.uid,
            uploadedByName: user.displayName || user.email || "Team member",
            createdAt: Date.now(),
          });
        } catch (err) {
          setError(`Upload failed: ${(err as Error).message}`);
        } finally {
          setUploading((n) => n - 1);
        }
      })();
    }
    if (fileInput.current) fileInput.current.value = "";
  }

  if (!job) return <p className="text-sm text-steel">Loading job…</p>;
  if (peek) {
    const when = job.startAt
      ? new Date(job.startAt).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) +
        (job.endAt ? ` – ${new Date(job.endAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "")
      : "Unscheduled";
    return (
      <div className="mx-auto max-w-xl space-y-3">
        <button className="text-xs text-steel hover:text-ink" onClick={onBack}>
          ← Back
        </button>
        <Card className="space-y-1">
          <h1 className="text-lg font-bold leading-tight">{job.title}</h1>
          <p className="text-xs text-steel">{when}</p>
          {job.customerName ? <p className="text-sm text-ink">{job.customerName}</p> : null}
          {job.address ? (
            <a className="block text-sm text-brandorange-dark" href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`} target="_blank" rel="noreferrer">
              {job.address}
            </a>
          ) : null}
          <p className="text-xs text-steel">Someone else's job: you can see when and where, not the details.</p>
        </Card>
      </div>
    );
  }

  const mapsUrl = job.address
    ? `https://maps.google.com/?q=${encodeURIComponent(job.address)}`
    : null;

  function fmtWhen() {
    if (!job!.startAt) return "Unscheduled";
    const d = new Date(job!.startAt);
    return (
      d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) +
      ", " +
      d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) +
      (job!.endAt
        ? " – " + new Date(job!.endAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
        : "")
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <button className="text-xs text-steel hover:text-ink" onClick={onBack}>
              ← Jobs
            </button>
            {role && role !== "tech" && !editing ? (
              <Button variant="secondary" className="px-2.5 py-0.5 text-xs" onClick={() => setEditing(true)}>
                Edit job
              </Button>
            ) : null}
          </div>
          <h1 className="text-lg font-bold leading-tight">{job.title}</h1>
          <p className="text-xs text-steel">
            {fmtWhen()}
            {job.poNumber ? ` · PO ${job.poNumber}` : ""}
            {job.payMode === "piece" ? " · piece rate" : ""}
            {workflow.approveToStart ? (job.customerApproval ? ` · OK'd by ${job.customerApproval.byName || "the customer"}` : " · waiting for the customer's OK") : ""}
          </p>
          {workflow.approveToStart && !job.customerApproval && role !== "tech" && job.status === "scheduled" ? (
            <button className="text-xs text-brandorange-dark underline" onClick={() => void call("setCustomerApproval", { tenantId: tenantId ?? "", jobId, approved: true })}>
              Customer OK'd it (by phone or email)
            </button>
          ) : null}
          {job.projectId && projectName ? (
            <Link href={`/projects/${job.projectId}`} className="text-xs text-brandorange-dark hover:underline">
              {projectName}
              {job.lot ? ` · ${job.lot}` : ""} →
            </Link>
          ) : job.lot ? (
            <p className="text-xs text-steel">{job.lot}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className={cn("rounded-full px-2 py-0.5 text-xs", statusStyle[job.status])}>
            {job.status.replace("_", " ")}
          </span>
          {tenantId && (role !== "tech" || job.assignedUids.includes(user?.uid ?? "")) ? (
            <JobSteps
              tenantId={tenantId}
              jobId={jobId}
              job={job}
              workflow={workflow}
              signoffRequired={signoffMode === "required"}
              photos={photos}
              onTakePhoto={() => fileInput.current?.click()}
              onNote={setStepNote}
            />
          ) : null}
          {dryRunLine(job) ? <span className="max-w-56 truncate text-[11px] text-danger" title={dryRunLine(job)}>{dryRunLine(job)}</span> : null}
        </div>
      </div>
      {stepNote ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-success/10 px-3 py-2 text-sm text-ink">
          <span className="flex-1">{stepNote}</span>
          {/invoice/i.test(stepNote) && show("payment") ? (
            <Button variant="ghost" className="px-2.5 py-0.5 text-xs" onClick={() => document.getElementById("job-payment")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
              Go to payment ↓
            </Button>
          ) : null}
          <button className="text-xs text-steel hover:text-ink" onClick={() => setStepNote("")} aria-label="Dismiss">
            ✕
          </button>
        </div>
      ) : null}
      {job.status === "in_progress" && job.arrivedAt && ((workflow.arrivePhotos && !photos.some((p) => p.createdAt >= job.arrivedAt!)) || (workflow.arriveNotes && !notes.some((n) => n.createdAt >= job.arrivedAt!))) ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brandorange/40 bg-brandorange/10 px-3 py-2 text-sm text-ink">
          <span className="flex-1">On site: take photos of what you found, and note anything the office should know.</span>
          {workflow.arrivePhotos && !photos.some((p) => p.createdAt >= job.arrivedAt!) ? (
            <Button variant="ghost" className="px-2.5 py-0.5 text-xs" onClick={() => fileInput.current?.click()}>
              Take photos
            </Button>
          ) : null}
          {workflow.arriveNotes && !notes.some((n) => n.createdAt >= job.arrivedAt!) ? (
            <Button variant="ghost" className="px-2.5 py-0.5 text-xs" onClick={() => document.getElementById("job-notes")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
              Add a note
            </Button>
          ) : null}
        </div>
      ) : null}

      {editing && tenantId ? <JobEditCard tenantId={tenantId} jobId={jobId} job={job} onDone={() => setEditing(false)} /> : null}

      <Card className={editing ? "hidden" : undefined}>
        {job.note ? (
          <div className="mb-3 border-b border-steel/15 pb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-steel">Requested</p>
            <p className="whitespace-pre-wrap text-sm text-ink">{job.note}</p>
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-steel">Customer</p>
            {customer ? (
              <>
                <p className="text-sm font-medium text-ink">{customer.displayName}</p>
                {customer.phone ? (
                  <>
                    <a href={`tel:${customer.phone}`} className="mt-0.5 flex items-center gap-1 text-sm text-brandorange-dark">
                      <Phone size={14} /> {customer.phone}
                    </a>
                    <div className={cn("mt-1.5 flex flex-wrap gap-1.5", !show("texts") && "hidden")}>
                      {job.status !== "done" && job.status !== "canceled" ? (
                        <Button
                          variant="secondary"
                          className="px-2 py-1 text-xs"
                          busy={texting === "on_my_way"}
                          onClick={() => void sendText("on_my_way")}
                        >
                          <MessageSquareText size={13} /> Text: on my way
                        </Button>
                      ) : null}
                      {job.status === "scheduled" ? (
                        <Button
                          variant="ghost"
                          className="px-2 py-1 text-xs"
                          busy={texting === "rep_intro"}
                          onClick={() => void sendText("rep_intro")}
                        >
                          Text: who's coming
                        </Button>
                      ) : null}
                    </div>
                    {textNote ? <p className="mt-1 text-[11px] text-steel">{textNote}</p> : null}
                  </>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-steel">No customer attached.</p>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-steel">Location</p>
            {job.address ? (
              <a
                href={mapsUrl!}
                target="_blank"
                rel="noreferrer"
                className="mt-0.5 flex items-start gap-1 text-sm text-brandorange-dark"
              >
                <MapPin size={14} className="mt-0.5 shrink-0" /> {job.address}
              </a>
            ) : (
              <p className="text-sm text-steel">No address yet — new lot? Drop a pin instead.</p>
            )}
            <button
              className="mt-1 text-xs text-brandorange-dark underline hover:text-brandorange"
              onClick={() => setPinOpen((v) => !v)}
            >
              {pinOpen ? "Close map" : job.lat != null ? "Adjust pin" : "Drop a pin"}
            </button>
            {job.lat != null && !pinOpen ? (
              <span className="ml-2 text-[11px] text-success">pinned ✓</span>
            ) : null}
          </div>
        </div>
        {pinOpen ? (
          <div className="mt-3 border-t border-steel/15 pt-3">
            <PinDrop
              initial={job.lat != null && job.lng != null ? { lat: job.lat, lng: job.lng } : null}
              home={null}
              onSave={async (pin) => {
                await updateDoc(doc(db, paths.tenantJob(tenantId!, jobId)), {
                  lat: pin.lat,
                  lng: pin.lng,
                  geocodeTriedAt: Date.now(),
                  ...editStamp(user),
                });
                setPinOpen(false);
              }}
              onCancel={() => setPinOpen(false)}
            />
          </div>
        ) : null}
      </Card>

      {job.projectItem ? (
        <Card>
          <div className="flex items-baseline justify-between gap-2">
            <CardTitle className="mb-0">{job.projectItem.name}</CardTitle>
            <span className="text-xs text-steel">
              bid scope ·{" "}
              {(job.projectItem.lines.reduce((s, l) => s + Math.round(l.quantity * l.unitPriceCents), 0) / 100).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </span>
          </div>
          <div className="mt-1 space-y-0.5 text-sm">
            {job.projectItem.lines.map((l, i) => (
              <div key={i} className="flex justify-between gap-2 text-ink">
                <span className="min-w-0 truncate">
                  {l.quantity} × {l.description}
                </span>
              </div>
            ))}
            {job.projectItem.lines.length === 0 ? <p className="text-xs text-steel">No lines on this item yet.</p> : null}
          </div>
        </Card>
      ) : null}

      {tenantId && show("work") ? <JobWorkDone tenantId={tenantId} jobId={jobId} job={job} work={work} /> : null}
      {job.jobWalk ? (
        <Card className="space-y-1 text-sm">
          <CardTitle className="mb-0">
            Job walk · {job.jobWalk.byName || "tech"} · {new Date(job.jobWalk.doneAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </CardTitle>
          {job.jobWalk.findings ? <p className="whitespace-pre-wrap text-ink">{job.jobWalk.findings}</p> : null}
          {job.jobWalk.notes.length ? (
            <ul className="list-disc pl-5 text-xs text-ink">
              {job.jobWalk.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          ) : null}
          <p className="text-[11px] text-steel">
            {job.jobWalk.photos} photo{job.jobWalk.photos === 1 ? "" : "s"} on the walk ·{" "}
            <Link href={`/jobs/${job.jobWalk.jobId}`} className="text-brandorange-dark hover:underline">
              open the walk
            </Link>
            {" "}· a new estimate below can start from these notes.
          </p>
        </Card>
      ) : null}
      {tenantId && show("work") && job.status !== "canceled" ? <JobLoads tenantId={tenantId} jobId={jobId} job={job} /> : null}

      {role && ROLE_RANK[role] >= 2 && !job.parentJobId ? (
        <JobChildren tenantId={tenantId!} jobId={jobId} parent={job} />
      ) : null}
      {role && ROLE_RANK[role] >= 2 ? <OrderedParts tenantId={tenantId!} jobId={jobId} /> : null}
      {role && ROLE_RANK[role] >= 2 && job.lat != null && job.lng != null ? (
        <AvailableTechs tenantId={tenantId!} jobId={jobId} lat={job.lat} lng={job.lng} />
      ) : null}
      {job.status !== "canceled" && show("estimates") ? (
        <JobEstimates
          tenantId={tenantId!}
          jobId={jobId}
          walk={job.jobWalk}
          payerId={job.billToCustomerId ?? job.customerId}
          onInvoiced={() => setBillingRev((n) => n + 1)}
        />
      ) : null}
      {tenantId && show("payment") ? <JobPay tenantId={tenantId} jobId={jobId} rev={billingRev} /> : null}
      <div id="job-payment" />
      {job.status !== "canceled" && show("payment") ? (
        <JobPayment
          key={billingRev}
          tenantId={tenantId!}
          jobId={jobId}
          // The bid item's lines plus what was logged as billable on site (already taken from stock).
          startLines={[
            ...(job.projectItem?.lines ?? []),
            ...work.filter((w) => w.billable).map((w) => ({ description: w.description, quantity: w.quantity, unitPriceCents: w.unitPriceCents })),
          ]}
        />
      ) : null}
      {job.status !== "canceled" && show("signoff") && signoffMode !== "off" && (job.status !== "scheduled" || job.signedOffAt) ? (
        <JobSignoff tenantId={tenantId!} jobId={jobId} required={signoffMode === "required"} />
      ) : null}
      {job.status !== "canceled" && user && show("expenses") ? (
        <JobExpenses tenantId={tenantId!} jobId={jobId} uid={user.uid} isTech={role === "tech"} />
      ) : null}
      {show("agreements") ? <JobAgreements tenantId={tenantId!} jobId={jobId} customerId={job.customerId} /> : null}
      {tenantId && role !== "tech" && can("customers.senddocs") ? (
        <SendDocuments tenantId={tenantId} customerId={job.customerId ?? null} projectId={job.projectId ?? null} jobId={jobId} suggestedEmails={customer?.email ? [customer.email] : []} />
      ) : null}
      {tenantId && role !== "tech" ? <TodoCard tenantId={tenantId} link={{ kind: "job", id: jobId, label: job.title.slice(0, 200) }} title="To-dos for this job" /> : null}
      {job.parentJobId ? (
        <Link href={`/jobs/${job.parentJobId}`} className="block text-xs text-brandorange-dark hover:underline">
          ← Part of a larger job — open the parent
        </Link>
      ) : null}

      {(role === "owner" || role === "admin") && hasFeature("jobProfit") ? <JobInsights tenantId={tenantId!} jobId={jobId} /> : null}

      <Card className={show("photos") ? undefined : "hidden"}>
        <div className="flex items-center justify-between">
          <CardTitle className="mb-0">Photos ({photos.length})</CardTitle>
          <Button
            variant="secondary"
            className="px-3 py-1 text-xs"
            onClick={() => fileInput.current?.click()}
            busy={uploading > 0}
          >
            <Camera size={14} /> {uploading > 0 ? `Uploading ${uploading}…` : "Add photos"}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => void uploadPhotos(e.target.files)}
          />
        </div>
        {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
        {photos.length > 0 ? (
          <div className="mt-2 grid grid-cols-3 gap-2">
            {photos.map((p) =>
              p.url ? (
                <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="block">
                  <img
                    src={p.url}
                    alt=""
                    loading="lazy"
                    className="aspect-square w-full rounded-lg border border-steel/20 object-cover"
                  />
                </a>
              ) : (
                <div key={p.id} className="aspect-square animate-pulse rounded-lg bg-steel/15" />
              ),
            )}
          </div>
        ) : (
          <p className="mt-2 text-xs text-steel">
            Before/after shots, serial numbers, damage documentation — it all lives on the job.
          </p>
        )}
      </Card>

      <Card id="job-notes" className={show("notes") ? undefined : "hidden"}>
        <CardTitle>Field notes</CardTitle>
        <div className="mb-3 flex gap-2">
          <Input
            placeholder="What happened on site?"
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void addNote();
            }}
          />
          <Button onClick={() => void addNote()} disabled={!noteBody.trim()}>
            Add
          </Button>
        </div>
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="rounded-lg bg-cream px-3 py-2">
              <p className="whitespace-pre-wrap text-sm text-ink">{n.body}</p>
              <p className="mt-0.5 text-[11px] text-steel">
                {n.authorName} ·{" "}
                {new Date(n.createdAt).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </p>
            </div>
          ))}
          {notes.length === 0 ? <p className="text-xs text-steel">No notes yet.</p> : null}
        </div>
      </Card>
    </div>
  );
}
